import React, { useEffect, useMemo, useState } from "react";
import { Pod24AdminLayout } from "../../components/Pod24Admin/Pod24AdminLayout";
import { Button } from "../../components/UI/Button";
import Table from "../../components/UI/Table";
import ConfirmMessageModal from "../../components/UI/ConfirmMessageModal";
import NotificationsList from "../../components/UI/NotificationList";
import { useNotifications } from "../../hooks/use-notifications";
import { adminMachineApiService } from "../../../infrastructure/api/admin/admin-machine-api-service";
import { adminCompanyApiService } from "../../../infrastructure/api/admin/admin-company-api-service";
import { AdminRouterService } from "../../../infrastructure/api/admin/admin-router-service";
import { adminMachineProductUseCase } from "../../../di/container";
import type { CompanyWithStatus } from "../../../domain/types/admin-company";
import type { MachineProductEntity } from "../../../domain/types/machine-product";
import type { MachineEntity } from "../../../domain/types/machine";
import type { RouterEntity } from "../../../domain/types/router";
import { Pencil, Trash2 } from "lucide-react";

const routerService = new AdminRouterService();

/**
 * 알림에 띄울 문구를 만든다.
 *
 * `String(error)` 를 그대로 쓰면 "AxiosError: Network Error" 같은 게 사용자에게 보인다.
 * 무엇을 하다 실패했는지와, 손쓸 수 있는 다음 행동을 알려준다.
 */
const toMessage = (what: string, error: unknown): string => {
  const raw = error instanceof Error ? error.message : String(error);
  if (/Network Error|ERR_NETWORK|ECONNREFUSED|Failed to fetch/i.test(raw)) {
    return `${what} — 서버에 연결하지 못했습니다. 백엔드가 실행 중인지 확인해주세요.`;
  }
  return `${what} — ${raw}`;
};

/**
 * 설치 장비 관리.
 *
 * 업체에 실제로 놓인 장비 한 대 한 대를 다룬다(장비 OID 관리가 다루는 "제품"과 구분된다).
 * - 탐색되지 않는 장비 등록: 공유 폴더에 로그만 남기는 장비는 IP·MAC 이 없어 라우터가 찾지 못한다.
 * - 별칭: 같은 모델이 여러 대일 때 사람이 구분할 유일한 수단.
 * - MES 노출: Fiery 처럼 한 대가 두 장비로 잡힐 때 한쪽을 감춘다. 수집은 계속된다.
 */
const InstalledMachineManagement: React.FC = () => {
  const { notifications, addItem: addNotification } = useNotifications();

  const [companies, setCompanies] = useState<CompanyWithStatus[]>([]);
  const [routers, setRouters] = useState<RouterEntity[]>([]);
  const [products, setProducts] = useState<MachineProductEntity[]>([]);
  const [machines, setMachines] = useState<MachineEntity[]>([]);

  const [companyId, setCompanyId] = useState<number | "">("");
  const [routerId, setRouterId] = useState<number | "">("");
  const [machineProductId, setMachineProductId] = useState<number | "">("");
  const [alias, setAlias] = useState("");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingAlias, setEditingAlias] = useState("");
  const [savingAlias, setSavingAlias] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmSubmit, setConfirmSubmit] = useState<() => void>(() => {});

  /** 탐색되지 않는 프로토콜만 등록 대상이다. SNMP 등은 라우터가 알아서 찾는다. */
  const offlineProducts = useMemo(
    () => products.filter((p) => p.protocol === "SHARED_FOLDER"),
    [products],
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [companyPage, productList] = await Promise.all([
          adminCompanyApiService.listCompaniesWithStatus({ page: 0, size: 200 }),
          adminMachineProductUseCase.getAll(),
        ]);
        setCompanies(companyPage.data?.items ?? []);
        setProducts(productList);
      } catch (error) {
        addNotification(toMessage("회사·장비 제품 목록을 불러오지 못했습니다", error));
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  // 회사를 고르면 그 회사의 라우터를 불러온다. 라우터가 정해져야 장비를 어디에 붙일지 결정된다.
  useEffect(() => {
    setRouterId("");
    setRouters([]);
    setMachines([]);
    if (companyId === "") return;

    const load = async () => {
      try {
        const response = await routerService.getRouters(Number(companyId));
        setRouters(response.data ?? []);
      } catch (error) {
        addNotification(toMessage("라우터 목록을 불러오지 못했습니다", error));
      }
    };
    void load();
  }, [companyId]);

  const loadMachines = async (id: number) => {
    try {
      const response = await adminMachineApiService.getMachinesByRouter(id);
      setMachines(response.data ?? []);
    } catch (error) {
      addNotification(toMessage("장비 목록을 불러오지 못했습니다", error));
    }
  };

  useEffect(() => {
    if (routerId === "") {
      setMachines([]);
      return;
    }
    void loadMachines(Number(routerId));
  }, [routerId]);

  const canSubmit =
    companyId !== "" && routerId !== "" && machineProductId !== "" && !submitting;

  const handleRegister = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await adminMachineApiService.registerInstalledMachine({
        companyId: Number(companyId),
        routerId: Number(routerId),
        machineProductId: Number(machineProductId),
        alias: alias.trim() || null,
      });
      addNotification("장비를 등록했습니다. 라우터가 다음 수집 주기에 받아갑니다.");
      setAlias("");
      setMachineProductId("");
      await loadMachines(Number(routerId));
    } catch (error) {
      addNotification(toMessage("장비를 등록하지 못했습니다", error));
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (machine: MachineEntity) => {
    setEditingId(Number(machine.id));
    setEditingAlias(machine.alias ?? "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingAlias("");
  };

  const saveAlias = async (machineId: number) => {
    setSavingAlias(true);
    try {
      await adminMachineApiService.updateMachineAlias(
        machineId,
        editingAlias.trim() || null,
      );
      addNotification("별칭을 저장했습니다.");
      cancelEdit();
      if (routerId !== "") await loadMachines(Number(routerId));
    } catch (error) {
      addNotification(toMessage("별칭을 저장하지 못했습니다", error));
    } finally {
      setSavingAlias(false);
    }
  };

  const toggleVisibility = async (machine: MachineEntity) => {
    const next = !machine.hiddenInMes;
    setTogglingId(Number(machine.id));
    try {
      await adminMachineApiService.updateMachineVisibility(Number(machine.id), next);
      addNotification(next ? "MES 에서 감췄습니다." : "MES 에 다시 표시합니다.");
      if (routerId !== "") await loadMachines(Number(routerId));
    } catch (error) {
      addNotification(toMessage("노출 설정을 바꾸지 못했습니다", error));
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = (machine: MachineEntity) => {
    const name = machine.alias || machine.machineProduct?.machineName || "장비";
    setConfirmMessage(`"${name}"을(를) 삭제할까요?`);
    setConfirmSubmit(() => async () => {
      // 모달을 먼저 닫는다. 확인 버튼은 제출 함수만 부르고 스스로 닫지 않아서,
      // 닫지 않으면 사용자가 안 지워진 줄 알고 다시 눌러 "이미 없음" 오류를 본다.
      setConfirmMessage("");
      setConfirmSubmit(() => {});
      try {
        await adminMachineApiService.deleteMachine(Number(machine.id));
        addNotification("삭제했습니다.");
        if (routerId !== "") await loadMachines(Number(routerId));
      } catch (error) {
        addNotification(toMessage("장비를 삭제하지 못했습니다", error));
      }
    });
  };

  const selectClass =
    "border border-[#e0e0e0] rounded-[8px] px-3 py-2 text-[13px] w-full focus:border-[#4285F4] focus:outline-none disabled:bg-[#f5f5f5] disabled:text-[#bbb]";

  return (
    <Pod24AdminLayout>
      {confirmMessage && (
        <ConfirmMessageModal
          message={confirmMessage}
          setMessage={setConfirmMessage}
          submitFunction={confirmSubmit}
        />
      )}
      <NotificationsList notifications={notifications} />

      <div className="mb-6">
        <h1
          className="text-[22px] font-bold text-[#222]"
          style={{ fontFamily: "Pretendard" }}
        >
          설치 장비 관리
        </h1>
        <p className="text-[13px] text-[#888] mt-1">
          업체에 설치된 장비를 관리합니다. 탐색되지 않는 장비를 등록하고, 별칭과 MES 노출을 조정합니다.
        </p>
      </div>

      <div className="mb-4 bg-[#EBF3FF] text-[#4285F4] border border-[#4285F4]/20 rounded-[8px] px-4 py-3 text-[13px] leading-relaxed">
        일반 장비는 라우터가 네트워크를 훑어 자동으로 등록합니다. 공유 폴더에 로그만 남기는
        장비는 IP·MAC 이 없어 탐색되지 않으므로 여기서 등록해야 하며, 등록하면 라우터가
        다음 수집 주기에 받아갑니다. IP 와 MAC 은 서버가 대신 채웁니다.
      </div>

      {loading ? (
        <div className="text-center py-10 text-[#888] text-[14px]">불러오는 중...</div>
      ) : (
        <>
          <h2 className="text-[16px] font-bold text-[#222] mt-6">장비 추가</h2>
          <div className="bg-white border border-[#e0e0e0] rounded-[12px] p-4 mb-6 mt-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-medium text-[#555] mb-1.5">
                  회사
                </label>
                <select
                  className={selectClass}
                  value={companyId}
                  onChange={(e) =>
                    setCompanyId(e.target.value === "" ? "" : Number(e.target.value))
                  }
                >
                  <option value="">선택하세요</option>
                  {companies.map((c) => (
                    <option key={c.companyId} value={c.companyId}>
                      {c.companyName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[12px] font-medium text-[#555] mb-1.5">
                  라우터
                </label>
                <select
                  className={selectClass}
                  value={routerId}
                  disabled={companyId === ""}
                  onChange={(e) =>
                    setRouterId(e.target.value === "" ? "" : Number(e.target.value))
                  }
                >
                  <option value="">
                    {companyId === ""
                      ? "회사를 먼저 선택하세요"
                      : routers.length === 0
                        ? "등록된 라우터가 없습니다"
                        : "선택하세요"}
                  </option>
                  {routers.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.routerName || `라우터 #${r.id}`}
                      {r.ipAddress ? ` (${r.ipAddress})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[12px] font-medium text-[#555] mb-1.5">
                  장비 제품
                </label>
                <select
                  className={selectClass}
                  value={machineProductId}
                  onChange={(e) =>
                    setMachineProductId(
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                >
                  <option value="">
                    {offlineProducts.length === 0
                      ? "공유 폴더 장비 제품이 없습니다"
                      : "선택하세요"}
                  </option>
                  {offlineProducts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.manufacturer} {p.machineName || p.machineDescription}
                    </option>
                  ))}
                </select>
                {offlineProducts.length === 0 && (
                  <p className="text-[12px] text-[#888] mt-1.5">
                    먼저 장비 OID 관리에서 프로토콜이 "공유 폴더"인 제품을 등록하세요.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[12px] font-medium text-[#555] mb-1.5">
                  별칭 <span className="text-[#888] font-normal">(선택)</span>
                </label>
                <input
                  type="text"
                  className={selectClass}
                  placeholder="예: 재단기 1호"
                  value={alias}
                  maxLength={100}
                  onChange={(e) => setAlias(e.target.value)}
                />
                <p className="text-[12px] text-[#888] mt-1.5">
                  같은 모델이 여러 대일 때 화면에서 구분하는 이름입니다. 비우면 장비 제품
                  이름으로 표시됩니다.
                </p>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <Button size="sm" disabled={!canSubmit} onClick={handleRegister}>
                {submitting ? "등록 중..." : "장비 등록"}
              </Button>
            </div>
          </div>

          {routerId !== "" && (
            <div>
              <h2 className="text-[16px] font-bold text-[#222]">
                이 라우터의 장비
              </h2>
              <p className="text-[13px] text-[#888] mt-1 mb-3">
                장비 제품은 라우터가 판별한 값이라 고칠 수 없습니다. 별칭은 비우면 장비 제품 이름이
                대신 나옵니다. <b className="font-medium text-[#555]">MES 노출</b>을 끄면 업체 화면에서만
                감춰지고 <b className="font-medium text-[#555]">수집과 KPI 는 그대로 돌아갑니다</b> —
                Fiery 처럼 한 대의 프린터가 두 장비로 잡힐 때 한쪽을 끄면 됩니다.
              </p>
              {machines.length === 0 ? (
                <div className="text-center py-10 text-[#888] text-[14px]">
                  등록된 장비가 없습니다.
                </div>
              ) : (
                <div className="text-[12px] overflow-x-auto">
                  <Table
                    columnWidths={["180px", "170px", "90px", "100px", "150px", "60px"]}
                    isPaddingRightEnabled={false}
                    tabledata={{
                      head: [
                        <>장비 제품</>,
                        <>별칭</>,
                        <>MES 노출</>,
                        <>프로토콜</>,
                        <>식별자</>,
                        <>삭제</>,
                      ],
                      body: machines.map((m) => [
                        // 장비 제품은 라우터가 판별한 값이라 여기서 고치지 않는다.
                        <>
                          <span className="block truncate text-[#222]">
                            {m.machineProduct?.machineName ||
                              m.machineProduct?.machineDescription ||
                              "-"}
                          </span>
                        </>,
                        <>
                          {editingId === Number(m.id) ? (
                            <div className="flex items-center gap-1.5">
                              <input
                                id={`alias-${m.id}`}
                                autoFocus
                                type="text"
                                maxLength={100}
                                className="border border-[#4285F4] rounded-[6px] px-2 py-1 text-[12px] w-[120px] focus:outline-none"
                                placeholder="예: 재단기 1호"
                                value={editingAlias}
                                onChange={(e) => setEditingAlias(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") void saveAlias(Number(m.id));
                                  if (e.key === "Escape") cancelEdit();
                                }}
                              />
                              <button
                                className="text-[#4285F4] hover:text-[#3367D6] text-[12px] font-medium disabled:text-[#bbb]"
                                disabled={savingAlias}
                                onClick={() => void saveAlias(Number(m.id))}
                              >
                                저장
                              </button>
                              <button
                                className="text-[#888] hover:text-[#555] text-[12px]"
                                onClick={cancelEdit}
                              >
                                취소
                              </button>
                            </div>
                          ) : (
                            <button
                              className="flex items-center gap-1.5 group text-left max-w-full"
                              onClick={() => startEdit(m)}
                            >
                              {m.alias ? (
                                <span className="block truncate text-[#222]">
                                  {m.alias}
                                </span>
                              ) : (
                                <span className="text-[#bbb]">이름 붙이기</span>
                              )}
                              <Pencil
                                size={12}
                                className="shrink-0 text-[#bbb] group-hover:text-[#4285F4]"
                              />
                            </button>
                          )}
                        </>,
                        <>
                          <button
                            className="flex items-center gap-1.5 disabled:opacity-50"
                            disabled={togglingId === Number(m.id)}
                            onClick={() => void toggleVisibility(m)}
                            title={
                              m.hiddenInMes
                                ? "MES 에서 감춰져 있습니다. 눌러서 다시 표시"
                                : "MES 에 표시되고 있습니다. 눌러서 감추기"
                            }
                          >
                            <span
                              className={`inline-block w-[28px] h-[16px] rounded-full relative transition-colors ${
                                m.hiddenInMes ? "bg-[#e0e0e0]" : "bg-[#4285F4]"
                              }`}
                            >
                              <span
                                className={`absolute top-[2px] w-[12px] h-[12px] rounded-full bg-white transition-all ${
                                  m.hiddenInMes ? "left-[2px]" : "left-[14px]"
                                }`}
                              />
                            </span>
                            <span className={m.hiddenInMes ? "text-[#bbb]" : "text-[#222]"}>
                              {m.hiddenInMes ? "숨김" : "표시"}
                            </span>
                          </button>
                        </>,
                        <>
                          {m.machineProduct?.protocol === "SHARED_FOLDER"
                            ? "공유 폴더"
                            : m.machineProduct?.protocol}
                        </>,
                        <>
                          <span className="text-[#888]">{m.macAddress}</span>
                        </>,
                        <>
                          <button
                            className="text-[#F24A4A] hover:text-[#D63939]"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(m);
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </>,
                      ]),
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Pod24AdminLayout>
  );
};

export default InstalledMachineManagement;
